/**
 * Per-unit (kostenplaats) budget statistics for the renovation tab.
 *
 * Mirrors the per-label chart but groups by unit ID. Three bars per unit:
 *  1. Strategic reserved — sum of `line.estimatedCostExclVat * (unit's
 *     share of the line)` across non-released lines that touch the unit.
 *     Lines without units are dropped from this view.
 *  2. Toegewezen op projectonderdelen — sum of unit distributions on
 *     `part.investmentAssignments`.
 *  3. Gefactureerd — pro-rated invoice spend per unit (mirrors the BE
 *     `getProjectUnitsOverview` math).
 *
 * Self-fetches the same investment + renovation endpoints the rest of the
 * tab already uses, then aggregates client-side. Filter chips let the user
 * narrow down to a subset of units.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { CaretDown, CaretUp, ChartBar, Cube, Funnel } from "@phosphor-icons/react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip as ChartTooltip,
} from "chart.js";

ChartJS.register(BarElement, CategoryScale, LinearScale, ChartTooltip, Legend);

interface UnitBudgetStatisticsSectionProps {
  buildingId: string;
}

interface UnitContributor {
  kind: "reserved" | "allocated" | "invoiced";
  amount: number;
  label: string;
  context?: string;
}

interface UnitBucket {
  unitId: string;
  reserved: number;
  partsAllocated: number;
  invoiced: number;
  contributors: UnitContributor[];
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("nl-BE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value || 0);

const BAR_COLOURS = {
  reserved: "#5B7304",
  partsAllocated: "#EA8162",
  invoiced: "#3B82F6",
};

export const UnitBudgetStatisticsSection = ({
  buildingId,
}: UnitBudgetStatisticsSectionProps) => {
  const { session } = useSession();

  const [items, setItems] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [excludedUnits, setExcludedUnits] = useState<Set<string>>(new Set());
  const [expandedBreakdown, setExpandedBreakdown] = useState<Set<string>>(
    new Set(),
  );

  const fetchAll = useCallback(async () => {
    if (!session || !buildingId) return;
    setIsLoading(true);
    try {
      const [itemsRes, projectsRes] = await Promise.all([
        fetchWithAuth(
          `${API_URL}/investment/building/${buildingId}/with-assignments`,
          { method: "GET" },
          session,
        ),
        fetchWithAuth(
          `${API_URL}/renovation/project/building/${buildingId}`,
          { method: "GET" },
          session,
        ),
      ]);
      if (itemsRes.ok) {
        const data = await itemsRes.json();
        setItems(Array.isArray(data?.items) ? data.items : []);
      }
      if (projectsRes.ok) {
        const data = await projectsRes.json();
        setProjects(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("[UnitBudgetStatisticsSection] fetch failed", err);
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener("renovation-project-refresh", handler);
    window.addEventListener("budget-buckets-changed", handler);
    return () => {
      window.removeEventListener("renovation-project-refresh", handler);
      window.removeEventListener("budget-buckets-changed", handler);
    };
  }, [fetchAll]);

  // ------- Aggregate per unit -------
  const buckets = useMemo<UnitBucket[]>(() => {
    const map = new Map<string, UnitBucket>();
    // Lookup table: investmentItem.id → its units. Used as the fallback when
    // an assignment has no explicit `unitDistributions` rows. We build this
    // once from the items endpoint instead of asking the projects endpoint
    // to deep-load `investmentAssignments.investmentItem.units`, which was
    // exploding the join cardinality and slowing the tab to a crawl.
    const itemUnitsById = new Map<number | string, string[]>();
    for (const it of items) {
      const ids = (Array.isArray(it?.units) ? it.units : [])
        .map((u: any) => u?.id)
        .filter((id: any): id is string => !!id);
      if (it?.id != null) itemUnitsById.set(it.id, ids);
    }
    const ensure = (unitId: string): UnitBucket => {
      let b = map.get(unitId);
      if (!b) {
        b = {
          unitId,
          reserved: 0,
          partsAllocated: 0,
          invoiced: 0,
          contributors: [],
        };
        map.set(unitId, b);
      }
      return b;
    };

    // 1) Strategic reserved per unit — equal-split estimatedCostExclVat
    // across each line's `units` many-to-many. Released lines excluded.
    for (const item of items) {
      if (item?.isReleased) continue;
      const total = Number(item?.estimatedCostExclVat) || 0;
      const units = Array.isArray(item?.units) ? item.units : [];
      const validUnits = units
        .map((u: any) => u?.id)
        .filter((id: any): id is string => !!id);
      if (validUnits.length === 0 || total <= 0) continue;
      const share = total / validUnits.length;
      const itemLabel =
        item?.subject ||
        item?.title ||
        (item?.id != null ? `Lijn #${item.id}` : "Investeringslijn");
      const labelChip = item?.budgetLabel?.label || item?.label?.label;
      for (const unitId of validUnits) {
        const bucket = ensure(unitId);
        bucket.reserved += share;
        bucket.contributors.push({
          kind: "reserved",
          amount: share,
          label: itemLabel,
          context: labelChip ? `Label: ${labelChip}` : undefined,
        });
      }
    }

    // 2 + 3) Walk parts → unit distributions
    for (const project of projects) {
      for (const part of project?.parts ?? []) {
        const partInvoiced =
          (part?.documentLines ?? [])
            .filter((line: any) => {
              const t = line?.document?.type;
              return t === "Factuur" || t === "INVOICE";
            })
            .reduce(
              (s: number, line: any) =>
                s +
                (Number(line?.totalInclVat) || Number(line?.totalExclVat) || 0),
              0,
            ) || 0;

        const assignments =
          part?.investmentAssignments ?? part?.budgetAssignments ?? [];

        // For each assignment, build a list of (unitId, amount) slices.
        // Prefer explicit unitDistributions; fall back to equal-splitting the
        // assignedAmount across the linked investmentItem.units when
        // distributions are missing (older assignments or pre-existing data).
        const assignmentSlices: Array<{
          a: any;
          slices: Array<{ unitId: string; amount: number }>;
        }> = [];
        for (const a of assignments) {
          const distributions = (a?.unitDistributions ?? []) as Array<any>;
          const explicit = distributions
            .map((d) => ({
              unitId: d?.unit?.id as string | undefined,
              amount: Number(d?.amount) || 0,
            }))
            .filter(
              (d): d is { unitId: string; amount: number } =>
                !!d.unitId && d.amount > 0,
            );

          if (explicit.length > 0) {
            assignmentSlices.push({ a, slices: explicit });
            continue;
          }

          // Fallback: equal-split assignedAmount across the investment item's
          // units. This keeps the per-unit chart populated even when explicit
          // distributions weren't recorded. We look up the item's units from
          // `itemUnitsById` (built from the items endpoint) so the projects
          // endpoint doesn't have to deep-load them.
          const assigned = Number(a?.assignedAmount) || 0;
          if (assigned <= 0) {
            assignmentSlices.push({ a, slices: [] });
            continue;
          }
          const validIds =
            itemUnitsById.get(a?.investmentItem?.id) ?? [];
          if (validIds.length === 0) {
            assignmentSlices.push({ a, slices: [] });
            continue;
          }
          const share = assigned / validIds.length;
          assignmentSlices.push({
            a,
            slices: validIds.map((unitId: string) => ({ unitId, amount: share })),
          });
        }

        // Aggregate slice totals across this part for invoice pro-rating.
        let partTotalSliceAmount = 0;
        for (const { slices } of assignmentSlices) {
          for (const s of slices) partTotalSliceAmount += s.amount;
        }

        const projectName = project?.name || project?.title || "Project";
        const partTitle = part?.title || part?.name || "Onderdeel";

        for (const { a, slices } of assignmentSlices) {
          const itemSubject =
            a?.investmentItem?.subject ||
            a?.investmentItem?.title ||
            (a?.investmentItem?.id != null
              ? `Lijn #${a.investmentItem.id}`
              : "Investeringslijn");

          for (const s of slices) {
            const bucket = ensure(s.unitId);
            bucket.partsAllocated += s.amount;
            bucket.contributors.push({
              kind: "allocated",
              amount: s.amount,
              label: `${projectName} — ${partTitle}`,
              context: itemSubject,
            });

            if (partInvoiced > 0 && partTotalSliceAmount > 0) {
              const invoiceShare =
                (s.amount / partTotalSliceAmount) * partInvoiced;
              bucket.invoiced += invoiceShare;
              bucket.contributors.push({
                kind: "invoiced",
                amount: invoiceShare,
                label: `${projectName} — ${partTitle}`,
                context: "Gefactureerd (pro rata)",
              });
            }
          }
        }
      }
    }

    return Array.from(map.values())
      .filter(
        (b) => b.reserved > 0 || b.partsAllocated > 0 || b.invoiced > 0,
      )
      .sort((a, b) => {
        const totalA = a.reserved + a.partsAllocated + a.invoiced;
        const totalB = b.reserved + b.partsAllocated + b.invoiced;
        return totalB - totalA;
      });
  }, [items, projects]);

  const toggleUnit = useCallback((id: string) => {
    setExcludedUnits((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const includeAll = useCallback(() => setExcludedUnits(new Set()), []);
  const excludeAll = useCallback(
    () => setExcludedUnits(new Set(buckets.map((b) => b.unitId))),
    [buckets],
  );

  const visibleBuckets = useMemo(
    () => buckets.filter((b) => !excludedUnits.has(b.unitId)),
    [buckets, excludedUnits],
  );

  const totals = useMemo(
    () =>
      visibleBuckets.reduce(
        (acc, b) => ({
          reserved: acc.reserved + b.reserved,
          partsAllocated: acc.partsAllocated + b.partsAllocated,
          invoiced: acc.invoiced + b.invoiced,
        }),
        { reserved: 0, partsAllocated: 0, invoiced: 0 },
      ),
    [visibleBuckets],
  );

  const chartData = useMemo(
    () => ({
      labels: visibleBuckets.map((b) => b.unitId),
      datasets: [
        {
          label: "Strategisch gereserveerd",
          data: visibleBuckets.map((b) => Math.round(b.reserved)),
          backgroundColor: BAR_COLOURS.reserved,
          borderRadius: 4,
        },
        {
          label: "Toegewezen op projectonderdelen",
          data: visibleBuckets.map((b) => Math.round(b.partsAllocated)),
          backgroundColor: BAR_COLOURS.partsAllocated,
          borderRadius: 4,
        },
        {
          label: "Gefactureerd",
          data: visibleBuckets.map((b) => Math.round(b.invoiced)),
          backgroundColor: BAR_COLOURS.invoiced,
          borderRadius: 4,
        },
      ],
    }),
    [visibleBuckets],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index" as const, intersect: false },
      plugins: {
        legend: { position: "top" as const, align: "end" as const },
        tooltip: {
          callbacks: {
            label: (ctx: any) =>
              `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`,
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 30 },
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: (val: any) => formatCurrency(Number(val)),
            font: { size: 11 },
          },
          grid: { color: "rgba(0,0,0,0.05)" },
        },
      },
    }),
    [],
  );

  return (
    <div className="flex flex-col gap-3">
      <header className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-2 transition-opacity hover:opacity-80"
        >
          {isExpanded ? (
            <CaretUp weight="bold" size={16} className="text-neutral-500" />
          ) : (
            <CaretDown weight="bold" size={16} className="text-neutral-500" />
          )}
          <ChartBar weight="duotone" size={18} className="text-moss-primary" />
          <p className="text-lg font-semibold">Budgetstatistieken per unit</p>
          <span className="text-neutral-secondary-mid text-sm">
            ({buckets.length} unit{buckets.length === 1 ? "" : "s"})
          </span>
        </button>
      </header>

      {isExpanded && (
        <div className="rounded-lg border border-neutral-200 bg-white p-4">
          {isLoading ? (
            <div className="text-neutral-secondary-mid py-8 text-center text-sm">
              Statistieken berekenen...
            </div>
          ) : buckets.length === 0 ? (
            <div className="text-neutral-secondary-mid py-8 text-center text-sm">
              Nog geen budgetdata om te tonen.
            </div>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center gap-1.5">
                <Funnel
                  size={12}
                  weight="duotone"
                  className="text-neutral-secondary-mid"
                />
                <span className="text-neutral-secondary-mid text-[11px] font-semibold uppercase tracking-wide">
                  Units
                </span>
                {buckets.map((b) => {
                  const active = !excludedUnits.has(b.unitId);
                  return (
                    <button
                      key={b.unitId}
                      type="button"
                      onClick={() => toggleUnit(b.unitId)}
                      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-colors ${
                        active
                          ? "border-moss-primary/40 bg-moss-primary/10 text-moss-primary"
                          : "border-neutral-200 bg-neutral-50 text-neutral-400 line-through"
                      }`}
                      title={
                        active
                          ? "Klik om deze unit te verbergen"
                          : "Klik om deze unit te tonen"
                      }
                    >
                      <Cube size={10} weight="duotone" />
                      {b.unitId}
                    </button>
                  );
                })}
                <span className="ml-auto flex gap-1">
                  <button
                    type="button"
                    onClick={includeAll}
                    className="text-moss-primary hover:underline text-[11px]"
                    disabled={excludedUnits.size === 0}
                  >
                    Toon alle
                  </button>
                  <span className="text-neutral-300 text-[11px]">·</span>
                  <button
                    type="button"
                    onClick={excludeAll}
                    className="text-neutral-secondary-mid hover:underline text-[11px]"
                    disabled={excludedUnits.size === buckets.length}
                  >
                    Verberg alle
                  </button>
                </span>
              </div>

              <div className="mb-3 grid grid-cols-3 gap-2">
                <KpiTile
                  color={BAR_COLOURS.reserved}
                  label="Strategisch gereserveerd"
                  value={totals.reserved}
                />
                <KpiTile
                  color={BAR_COLOURS.partsAllocated}
                  label="Op projectonderdelen"
                  value={totals.partsAllocated}
                />
                <KpiTile
                  color={BAR_COLOURS.invoiced}
                  label="Gefactureerd"
                  value={totals.invoiced}
                />
              </div>

              {visibleBuckets.length === 0 ? (
                <div className="text-neutral-secondary-mid py-6 text-center text-xs italic">
                  Alle units zijn verborgen — selecteer minstens één unit om
                  de grafiek te tonen.
                </div>
              ) : (
                <>
                  <div className="h-[340px]">
                    <Bar data={chartData} options={chartOptions} />
                  </div>

                  <div className="mt-4 border-t border-neutral-100 pt-3">
                    <p className="text-neutral-secondary-mid mb-2 text-[11px] font-semibold uppercase tracking-wide">
                      Verdeling per kostenplaats
                    </p>
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
                      {visibleBuckets.map((b) => {
                        const isOpen = expandedBreakdown.has(b.unitId);
                        const total =
                          b.reserved + b.partsAllocated + b.invoiced;
                        return (
                          <UnitBreakdownCard
                            key={b.unitId}
                            bucket={b}
                            total={total}
                            isOpen={isOpen}
                            onToggle={() =>
                              setExpandedBreakdown((prev) => {
                                const next = new Set(prev);
                                if (next.has(b.unitId)) next.delete(b.unitId);
                                else next.add(b.unitId);
                                return next;
                              })
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const UnitBreakdownCard = ({
  bucket,
  total,
  isOpen,
  onToggle,
}: {
  bucket: UnitBucket;
  total: number;
  isOpen: boolean;
  onToggle: () => void;
}) => {
  // Group contributors by kind, then collapse duplicate (label,context)
  // entries — multiple project parts can contribute the same row, summed.
  const grouped = useMemo(() => {
    const out: Record<UnitContributor["kind"], UnitContributor[]> = {
      reserved: [],
      allocated: [],
      invoiced: [],
    };
    const dedupe = new Map<string, UnitContributor>();
    for (const c of bucket.contributors) {
      const key = `${c.kind}|${c.label}|${c.context ?? ""}`;
      const existing = dedupe.get(key);
      if (existing) existing.amount += c.amount;
      else dedupe.set(key, { ...c });
    }
    for (const c of dedupe.values()) {
      if (c.amount <= 0) continue;
      out[c.kind].push(c);
    }
    out.reserved.sort((a, b) => b.amount - a.amount);
    out.allocated.sort((a, b) => b.amount - a.amount);
    out.invoiced.sort((a, b) => b.amount - a.amount);
    return out;
  }, [bucket.contributors]);

  return (
    <div className="rounded-md border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left transition-colors hover:bg-neutral-50"
      >
        <div className="flex min-w-0 items-center gap-2">
          <Cube
            size={14}
            weight="duotone"
            className="text-moss-primary shrink-0"
          />
          <span className="truncate text-xs font-semibold text-neutral-primary">
            {bucket.unitId}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-neutral-secondary-mid text-[11px] tabular-nums">
            {formatCurrency(total)}
          </span>
          {isOpen ? (
            <CaretUp size={12} className="text-neutral-400" />
          ) : (
            <CaretDown size={12} className="text-neutral-400" />
          )}
        </div>
      </button>

      <div className="grid grid-cols-3 gap-1 border-t border-neutral-100 px-3 py-1.5">
        <MiniStat
          color={BAR_COLOURS.reserved}
          label="Strat."
          value={bucket.reserved}
        />
        <MiniStat
          color={BAR_COLOURS.partsAllocated}
          label="Onderd."
          value={bucket.partsAllocated}
        />
        <MiniStat
          color={BAR_COLOURS.invoiced}
          label="Fact."
          value={bucket.invoiced}
        />
      </div>

      {isOpen && (
        <div className="border-t border-neutral-100 px-3 py-2">
          <BreakdownGroup
            color={BAR_COLOURS.reserved}
            title="Strategisch gereserveerd"
            entries={grouped.reserved}
          />
          <BreakdownGroup
            color={BAR_COLOURS.partsAllocated}
            title="Op projectonderdelen"
            entries={grouped.allocated}
          />
          <BreakdownGroup
            color={BAR_COLOURS.invoiced}
            title="Gefactureerd"
            entries={grouped.invoiced}
          />
        </div>
      )}
    </div>
  );
};

const MiniStat = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) => (
  <div className="flex flex-col">
    <span className="flex items-center gap-1 text-[9px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
    <span className="text-[11px] font-medium tabular-nums text-neutral-primary">
      {formatCurrency(value)}
    </span>
  </div>
);

const BreakdownGroup = ({
  color,
  title,
  entries,
}: {
  color: string;
  title: string;
  entries: UnitContributor[];
}) => {
  if (entries.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-[10px] font-semibold uppercase tracking-wide text-neutral-secondary-mid">
          {title}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5">
        {entries.map((c, i) => (
          <li
            key={`${c.label}-${c.context ?? ""}-${i}`}
            className="flex items-start justify-between gap-2 text-[11px] leading-tight"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-neutral-primary" title={c.label}>
                {c.label}
              </p>
              {c.context && (
                <p
                  className="truncate text-[10px] text-neutral-secondary-mid"
                  title={c.context}
                >
                  {c.context}
                </p>
              )}
            </div>
            <span className="shrink-0 tabular-nums text-neutral-primary">
              {formatCurrency(c.amount)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const KpiTile = ({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: number;
}) => (
  <div className="rounded-md border border-neutral-200 bg-white p-2.5">
    <div className="flex items-center gap-1.5">
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      <span className="text-neutral-secondary-mid text-[10px] font-semibold uppercase tracking-wide">
        {label}
      </span>
    </div>
    <div className="mt-0.5 text-base font-semibold tabular-nums text-neutral-primary">
      {formatCurrency(value)}
    </div>
  </div>
);

export default UnitBudgetStatisticsSection;
